/**
 * Synthetic product screens, one per group on /features.
 *
 * These drive the app-window preview beside the category rail. Most groups now
 * show one of these rather than a real capture: featureScreenshots.ts keeps
 * only two or three genuine hero shots per role, and everything else lands
 * here. That is deliberate — a mock conveys the *shape* of a screen (a stat
 * row, a work queue, a status per line) without publishing the screen itself.
 * See the header of featureData.ts for why.
 *
 * So: invent the data. Never transcribe a real screen's columns, counts or
 * layout into a mock — that would put back what the rewrite removed, just in a
 * lower resolution.
 *
 * Every entry is deliberately the same shape — a screen title, three stats and
 * five rows — so switching categories never changes the panel's height and the
 * layout doesn't jump under the pointer.
 */
export type Tone = "emerald" | "amber" | "teal" | "slate" | "rose";

export type Mock = {
  /** Title in the window chrome. */
  screen: string;
  stats: { label: string; value: string; delta?: string }[];
  rows: { a: string; b: string; c: string; tone: Tone }[];
  isMobile?: boolean;
};

export const MOCKS: Record<string, Mock> = {
  // ── Admin ────────────────────────────────────────────────────────────────
  "admin-overview": {
    screen: "Dashboard · Academic Year 2025-26",
    stats: [
      { label: "Students", value: "2,350", delta: "+4.2%" },
      { label: "Present today", value: "92.6%", delta: "+0.8%" },
      { label: "Collected today", value: "₹4.812L", delta: "38 receipts" },
    ],
    rows: [
      { a: "Attendance below 75%", b: "14 students flagged this week", c: "Review", tone: "amber" },
      { a: "Fees overdue", b: "62 families past the due date", c: "Follow up", tone: "rose" },
      { a: "Staff present", b: "114 of 120 punched in", c: "On track", tone: "emerald" },
      { a: "Open tickets", b: "5 awaiting a first response", c: "Pending", tone: "slate" },
      { a: "Results awaiting sign-off", b: "Class IX · Term II", c: "Your action", tone: "teal" },
    ],
  },
  "admin-people": {
    screen: "People · Students & Staff",
    stats: [
      { label: "Enrolled", value: "2,350", delta: "+184 this term" },
      { label: "In admission", value: "47", delta: "Pipeline" },
      { label: "Staff", value: "120", delta: "+3" },
    ],
    rows: [
      { a: "New enquiry received", b: "Class VI · walked in this morning", c: "New", tone: "teal" },
      { a: "Application under review", b: "Class IX · documents verified", c: "In review", tone: "amber" },
      { a: "Admission confirmed", b: "Class XI · record created", c: "Enrolled", tone: "emerald" },
      { a: "Transfer certificate", b: "Requested · awaiting clearance", c: "Processing", tone: "slate" },
      { a: "Teacher onboarded", b: "Subjects and sections allotted", c: "Active", tone: "emerald" },
    ],
  },
  "admin-academics": {
    screen: "Academics · Term II",
    stats: [
      { label: "Sections", value: "42", delta: "Active" },
      { label: "Subjects", value: "96", delta: "Allotted" },
      { label: "Results", value: "38", delta: "Published" },
    ],
    rows: [
      { a: "Timetable published", b: "All sections · clash-free", c: "Live", tone: "emerald" },
      { a: "Attendance marked", b: "40 of 42 sections, by 10:15", c: "On time", tone: "emerald" },
      { a: "Marks entry open", b: "Term II · closes Friday", c: "In progress", tone: "amber" },
      { a: "Report cards", b: "Class X · ready to print", c: "Ready", tone: "teal" },
      { a: "Consent form", b: "Field trip · 118 of 140 returned", c: "Collecting", tone: "slate" },
    ],
  },
  "admin-finance": {
    screen: "Finance · Fees & Payroll",
    stats: [
      { label: "Collected", value: "₹1.84Cr", delta: "This term" },
      { label: "Outstanding", value: "₹22.6L", delta: "62 families" },
      { label: "Payroll run", value: "₹38.4L", delta: "Processed" },
    ],
    rows: [
      { a: "Counter receipt issued", b: "Cash · printed at the desk", c: "Settled", tone: "emerald" },
      { a: "Online payment", b: "UPI · cleared and reconciled", c: "Settled", tone: "emerald" },
      { a: "Cheque deposited", b: "Awaiting bank clearance", c: "In clearing", tone: "amber" },
      { a: "Sibling concession", b: "Applied by rule, not by hand", c: "Applied", tone: "teal" },
      { a: "Salary disbursed", b: "Statutory deductions calculated", c: "Paid", tone: "emerald" },
    ],
  },
  "admin-operations": {
    screen: "Campus & Communication",
    stats: [
      { label: "Notices sent", value: "36", delta: "This month" },
      { label: "Open tickets", value: "5", delta: "2 overdue" },
      { label: "Facilities", value: "4", delta: "All connected" },
    ],
    rows: [
      { a: "Holiday declared", b: "Published to every family at once", c: "Delivered", tone: "emerald" },
      { a: "Event scheduled", b: "Annual day · on the shared calendar", c: "Upcoming", tone: "teal" },
      { a: "Parent complaint", b: "Ticket assigned to the front office", c: "Assigned", tone: "amber" },
      { a: "Website page updated", b: "Published by the school, not an agency", c: "Live", tone: "emerald" },
      { a: "Bus route changed", b: "Affected families notified", c: "Notified", tone: "slate" },
    ],
  },

  // ── Teacher ──────────────────────────────────────────────────────────────
  "teacher-day": {
    screen: "My Day · Today",
    stats: [
      { label: "My periods", value: "5", delta: "Today" },
      { label: "My sections", value: "4", delta: "Assigned" },
      { label: "Marked", value: "3 of 5", delta: "By 10:15" },
    ],
    rows: [
      { a: "Period 1 · attendance", b: "Marked from the classroom", c: "Done", tone: "emerald" },
      { a: "Period 2 · attendance", b: "Marked · 2 absentees flagged", c: "Done", tone: "emerald" },
      { a: "Period 4 · attendance", b: "Starts at 12:20", c: "Upcoming", tone: "teal" },
      { a: "Message from a parent", b: "Awaiting your reply", c: "Unread", tone: "amber" },
      { a: "Staff notice", b: "Relevant to your sections only", c: "New", tone: "slate" },
    ],
  },
  "teacher-classroom": {
    screen: "Classwork & Assessment",
    stats: [
      { label: "Homework set", value: "12", delta: "This week" },
      { label: "Submitted", value: "86%", delta: "+9%" },
      { label: "Marks pending", value: "1", delta: "Closes Friday" },
    ],
    rows: [
      { a: "Homework posted", b: "Visible to parents immediately", c: "Delivered", tone: "emerald" },
      { a: "Assignment due", b: "Submissions tracked per student", c: "Open", tone: "teal" },
      { a: "Class test marks", b: "Entered by the teacher who marked", c: "Saved", tone: "emerald" },
      { a: "Exam schedule", b: "Published to the section", c: "Published", tone: "teal" },
      { a: "Consent form", b: "Responses counted for you", c: "Collecting", tone: "amber" },
    ],
  },
  "teacher-app": {
    screen: "Teacher App",
    isMobile: true,
    stats: [
      { label: "Periods", value: "5", delta: "Today" },
      { label: "Marked", value: "3", delta: "In class" },
      { label: "Replies", value: "2", delta: "Pending" },
    ],
    rows: [
      { a: "Mark attendance", b: "At the front of the class", c: "Tap", tone: "emerald" },
      { a: "Post homework", b: "Between two periods", c: "Tap", tone: "teal" },
      { a: "Reply to a parent", b: "Without sharing your number", c: "2 new", tone: "amber" },
      { a: "Apply for leave", b: "Balance shown before you apply", c: "Open", tone: "slate" },
      { a: "This month's payslip", b: "Download as PDF", c: "Ready", tone: "emerald" },
    ],
  },
  "teacher-self": {
    screen: "Leave, Salary & Reports",
    stats: [
      { label: "Leave balance", value: "8", delta: "Casual" },
      { label: "Payslips", value: "12", delta: "Downloadable" },
      { label: "My sections", value: "4", delta: "Reportable" },
    ],
    rows: [
      { a: "Leave applied", b: "Balance checked before applying", c: "Submitted", tone: "teal" },
      { a: "Leave approved", b: "Reflected in the staff register", c: "Approved", tone: "emerald" },
      { a: "Payslip available", b: "Downloaded without asking HR", c: "Ready", tone: "emerald" },
      { a: "Attendance report", b: "Your sections only", c: "Generated", tone: "slate" },
      { a: "Report cards", b: "For the class you are responsible for", c: "Ready", tone: "teal" },
    ],
  },

  // ── Staff ────────────────────────────────────────────────────────────────
  "staff-workspace": {
    screen: "Staff Workspace",
    stats: [
      { label: "My modules", value: "6", delta: "Granted" },
      { label: "My tickets", value: "3", delta: "Open" },
      { label: "Circulars", value: "4", delta: "Unread" },
    ],
    rows: [
      { a: "Scoped home screen", b: "Built for your job, not the principal's", c: "Active", tone: "emerald" },
      { a: "Module access", b: "Granted per person, per module", c: "Verified", tone: "teal" },
      { a: "Staff circular", b: "Issued to the office team", c: "New", tone: "amber" },
      { a: "Ticket raised", b: "Owner assigned, closing time set", c: "Assigned", tone: "slate" },
      { a: "Internal message", b: "No personal numbers exchanged", c: "Unread", tone: "teal" },
    ],
  },
  "staff-app": {
    screen: "Staff App",
    isMobile: true,
    stats: [
      { label: "Punched in", value: "08:42", delta: "On campus" },
      { label: "Leave left", value: "6", delta: "Casual" },
      { label: "Payslip", value: "Ready", delta: "This month" },
    ],
    rows: [
      { a: "Biometric punch", b: "Geofenced to the campus", c: "Recorded", tone: "emerald" },
      { a: "Punch review", b: "Corrections raised from the phone", c: "Pending", tone: "amber" },
      { a: "Apply for leave", b: "Balance shown before applying", c: "Open", tone: "teal" },
      { a: "Payslip PDF", b: "Downloaded without visiting HR", c: "Ready", tone: "emerald" },
      { a: "Raise a ticket", b: "Tracked to a close", c: "Tap", tone: "slate" },
    ],
  },
  "staff-frontdesk": {
    screen: "Front Office · Counter",
    stats: [
      { label: "Receipts today", value: "38", delta: "Printed" },
      { label: "Counter total", value: "₹4.812L", delta: "Matches system" },
      { label: "Enquiries", value: "9", delta: "Logged today" },
    ],
    rows: [
      { a: "Cash received", b: "Receipt printed at the desk", c: "Settled", tone: "emerald" },
      { a: "Cheque received", b: "Logged against the child's plan", c: "In clearing", tone: "amber" },
      { a: "Online payment", b: "Same ledger as the counter", c: "Settled", tone: "emerald" },
      { a: "Walk-in enquiry", b: "Captured at the desk, not on paper", c: "Logged", tone: "teal" },
      { a: "Parent callback", b: "Promised, so it is on the queue", c: "Assigned", tone: "slate" },
    ],
  },
  "staff-facilities": {
    screen: "Library, Hostel & Facilities",
    stats: [
      { label: "Books issued", value: "214", delta: "This month" },
      { label: "Beds free", value: "18", delta: "Live" },
      { label: "Overdue", value: "7", delta: "Fines due" },
    ],
    rows: [
      { a: "Book issued", b: "Against the student's own record", c: "On loan", tone: "teal" },
      { a: "Return processed", b: "Fine calculated, not negotiated", c: "Returned", tone: "emerald" },
      { a: "Bed allotted", b: "Occupancy updates as you allot", c: "Occupied", tone: "emerald" },
      { a: "Fixture scheduled", b: "Team and equipment reserved", c: "Upcoming", tone: "slate" },
      { a: "Route assigned", b: "Stop mapped to the children on it", c: "Active", tone: "teal" },
    ],
  },

  // ── Student & Parent ─────────────────────────────────────────────────────
  "student-home": {
    screen: "Student & Parent Portal",
    stats: [
      { label: "Attendance", value: "94.2%", delta: "This term" },
      { label: "Periods today", value: "7", delta: "Timetable" },
      { label: "Notices", value: "3", delta: "Unread" },
    ],
    rows: [
      { a: "Present today", b: "Marked in period 1, at 08:55", c: "Present", tone: "emerald" },
      { a: "Absence recorded", b: "Parent alerted the same day", c: "Absent", tone: "rose" },
      { a: "Today's timetable", b: "Seven periods, by subject", c: "Live", tone: "teal" },
      { a: "Holiday declared", b: "Seen the moment it was published", c: "New", tone: "amber" },
      { a: "Annual day", b: "On the family calendar", c: "Upcoming", tone: "slate" },
    ],
  },
  "student-app": {
    screen: "Parent App",
    isMobile: true,
    stats: [
      { label: "Attendance", value: "94.2%", delta: "This term" },
      { label: "Homework", value: "2", delta: "Due today" },
      { label: "Fees due", value: "₹0", delta: "Paid" },
    ],
    rows: [
      { a: "Attendance alert", b: "On the day, not at term end", c: "Delivered", tone: "emerald" },
      { a: "Homework posted", b: "As the teacher set it", c: "2 due", tone: "amber" },
      { a: "Test scheduled", b: "Known in advance", c: "Upcoming", tone: "teal" },
      { a: "Circular received", b: "Not a note lost in the school bag", c: "New", tone: "slate" },
      { a: "Classroom chat", b: "With the teacher, in the app", c: "Open", tone: "teal" },
    ],
  },
  "student-academics": {
    screen: "Classwork, Exams & Results",
    stats: [
      { label: "Homework due", value: "2", delta: "This week" },
      { label: "Tests ahead", value: "3", delta: "Scheduled" },
      { label: "Results", value: "Published", delta: "Term II" },
    ],
    rows: [
      { a: "Homework due", b: "With the date the teacher set", c: "Due", tone: "amber" },
      { a: "Assignment submitted", b: "Recorded against the student", c: "Submitted", tone: "emerald" },
      { a: "Exam schedule", b: "Published ahead of the week", c: "Upcoming", tone: "teal" },
      { a: "Result published", b: "From the marks the teacher entered", c: "Available", tone: "emerald" },
      { a: "Report card", b: "Downloadable as a PDF", c: "Ready", tone: "teal" },
    ],
  },
  "student-fees": {
    screen: "Fees & Payments",
    stats: [
      { label: "Paid", value: "₹64,000", delta: "This year" },
      { label: "Due now", value: "₹0", delta: "Cleared" },
      { label: "Next instalment", value: "₹18,000", delta: "In 21 days" },
    ],
    rows: [
      { a: "Term II fee", b: "Paid online by UPI, at 11pm", c: "Paid", tone: "emerald" },
      { a: "Receipt stored", b: "Kept against the child, never lost", c: "Saved", tone: "emerald" },
      { a: "Sibling concession", b: "Applied to the plan by rule", c: "Applied", tone: "teal" },
      { a: "Next instalment", b: "Reminder sent before the due date", c: "Upcoming", tone: "slate" },
      { a: "Payment history", b: "Every term, in one list", c: "Complete", tone: "teal" },
    ],
  },
};

/** Shown if a group somehow has no mock of its own — the panel never blanks. */
export const DEFAULT_MOCK: Mock = MOCKS["admin-overview"];

export const TONE_CLASS: Record<Tone, string> = {
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-600/15",
  amber: "bg-amber-50 text-amber-700 ring-amber-600/15",
  teal: "bg-brand-50 text-brand-700 ring-brand-600/15",
  slate: "bg-slate-100 text-slate-600 ring-slate-500/15",
  rose: "bg-rose-50 text-rose-700 ring-rose-600/15",
};
